import React, { useMemo, useState } from 'react';
import { Users, Search as SearchIcon, Building2, User as UserIcon } from 'lucide-react';
import { formatDocument } from '../utils/formatDocument';
import AppHeader from '../components/layout/AppHeader';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import EntityLink from '../components/shared/EntityLink';
import { useProprietarios } from '../hooks/useProprietarios';

const TIPO_PROP_LABEL = {
  full_ownership: 'Plena',
  bare_ownership: 'Nua',
  usufruct: 'Usufruto',
  possession: 'Posse',
  judicial: 'Judicial',
};

function TipoBadge({ tipo }) {
  const pj = tipo === 'PJ';
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold inline-flex items-center gap-1 ${
        pj ? 'bg-indigo-100 text-indigo-800' : 'bg-emerald-100 text-emerald-800'
      }`}
      title={pj ? 'Pessoa Jurídica' : 'Pessoa Física'}
    >
      {pj ? <Building2 size={10} /> : <UserIcon size={10} />} {tipo}
    </span>
  );
}

function fmtArea(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 }) + ' m²';
}

function fmtPct(v) {
  if (v == null) return '—';
  return `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

export default function ProprietariosPage() {
  const { data, loading, error, refetch } = useProprietarios();
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState(''); // 'PJ' | 'PF' | ''
  const [areaFilter, setAreaFilter] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const areas = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    data.forEach((r) => { if (r.area_id && !map.has(r.area_id)) map.set(r.area_id, r.area_nome); });
    return Array.from(map.entries()).map(([id, nome]) => ({ id, nome })).sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    return data.filter((r) => {
      if (tipoFilter && r.tipo_pessoa !== tipoFilter) return false;
      if (areaFilter && r.area_id !== areaFilter) return false;
      if (needle) {
        const bag = [r.nome_display, r.imovel_nome, r.matricula, r.area_nome, r.contato_email, r.documento].filter(Boolean).join(' ').toLowerCase();
        if (!bag.includes(needle)) return false;
      }
      return true;
    });
  }, [data, search, tipoFilter, areaFilter]);

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case 'tipo':         va = a.tipo_pessoa; vb = b.tipo_pessoa; break;
        case 'nome':         va = a.nome_display; vb = b.nome_display; break;
        case 'documento':    va = a.documento; vb = b.documento; break;
        case 'imovel':       va = a.imovel_nome; vb = b.imovel_nome; break;
        case 'matricula':    va = a.matricula; vb = b.matricula; break;
        case 'area':         va = a.area_terreno_m2 == null ? null : Number(a.area_terreno_m2); vb = b.area_terreno_m2 == null ? null : Number(b.area_terreno_m2); break;
        case 'percentual':   va = a.percentual_participacao == null ? null : Number(a.percentual_participacao); vb = b.percentual_participacao == null ? null : Number(b.percentual_participacao); break;
        case 'propriedade':  va = a.tipo_propriedade; vb = b.tipo_propriedade; break;
        case 'area_negocio': va = a.area_nome; vb = b.area_nome; break;
        case 'contato':      va = a.tipo_pessoa === 'PJ' ? null : (a.contato_email || a.contato_mobile); vb = b.tipo_pessoa === 'PJ' ? null : (b.contato_email || b.contato_mobile); break;
        default: return 0;
      }
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb), 'pt-BR', { sensitivity: 'base' })
        : String(vb).localeCompare(String(va), 'pt-BR', { sensitivity: 'base' });
    });
    return arr;
  }, [filtered, sortField, sortDir]);

  const SortHeader = ({ field, label, align = 'text-left', minW = 80 }) => (
    <th
      className={`px-3 py-2 ${align} cursor-pointer hover:text-blue-700 select-none overflow-hidden resize-x`}
      style={{ minWidth: `${minW}px` }}
      onClick={() => handleSort(field)}
    >
      {label} {sortField === field && (sortDir === 'asc' ? '▲' : '▼')}
    </th>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader title="Proprietários" subtitle="Visão operacional" icon={Users} />
      <main className="w-full px-4 lg:px-6 2xl:px-8 py-4 lg:py-6 space-y-3">
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <SearchIcon size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar nome, matrícula, imóvel, email..."
              className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-blue-400"
            />
          </div>
          <div className="flex items-center gap-0.5 bg-slate-100 rounded p-0.5">
            {['', 'PJ', 'PF'].map((t) => (
              <button
                key={t || 'all'}
                onClick={() => setTipoFilter(t)}
                className={`px-2 py-1 text-xs rounded ${tipoFilter === t ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}
              >
                {t || 'Todos'}
              </button>
            ))}
          </div>
          <select
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
            className="px-2 py-2 border border-slate-200 rounded text-xs bg-white max-w-[200px]"
          >
            <option value="">Todas as áreas</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
          </select>
          <div className="text-[11px] text-slate-500 ml-auto">
            {loading ? '...' : `${filtered.length}${data ? ` / ${data.length}` : ''}`}
          </div>
        </div>

        {loading && <LoadingSpinner label="Carregando proprietários..." />}
        {!loading && error && <ErrorState message={error} onRetry={refetch} />}
        {!loading && !error && (!data || data.length === 0) && (
          <EmptyState
            message="Nenhum proprietário cadastrado"
            hint="Adicione proprietários na ficha dos imóveis."
            icon={Users}
          />
        )}
        {!loading && !error && data && data.length > 0 && filtered.length === 0 && (
          <EmptyState message="Nenhum proprietário bate com os filtros" icon={Users} />
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg">
            <div>
              <table className="w-full text-xs min-w-[1100px]">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 sticky top-0 z-10">
                <tr>
                  <SortHeader field="tipo" label="Tipo" minW={60} />
                  <SortHeader field="nome" label="Nome" minW={180} />
                  <SortHeader field="documento" label="Documento" minW={120} />
                  <SortHeader field="imovel" label="Imóvel" minW={160} />
                  <SortHeader field="matricula" label="Matrícula" minW={100} />
                  <SortHeader field="area" label="Área" align="text-right" minW={80} />
                  <SortHeader field="percentual" label="%" align="text-right" minW={60} />
                  <SortHeader field="propriedade" label="Propriedade" minW={100} />
                  <SortHeader field="area_negocio" label="Área de Negócio" minW={140} />
                  <SortHeader field="contato" label="Contato" minW={140} />
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const isPJ = r.tipo_pessoa === 'PJ';
                  return (
                    <tr key={r.proprietario_id} className="border-t border-slate-100 hover:bg-blue-50">
                      <td className="px-3 py-1.5"><TipoBadge tipo={r.tipo_pessoa} /></td>
                      <td className="px-3 py-1.5 max-w-[200px]">
                        {isPJ && r.empresa_id ? (
                          <EntityLink entityType="empresa" entityId={r.empresa_id} label={r.nome_display || '—'} showIcon={false} />
                        ) : !isPJ && r.contato_id ? (
                          <EntityLink entityType="contato" entityId={r.contato_id} label={r.nome_display || '—'} showIcon={false} />
                        ) : (
                          <span className="truncate block" title={r.nome_display || ''}>{r.nome_display || '—'}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-slate-600 text-[11px] whitespace-nowrap">
                        {r.documento ? formatDocument(r.documento) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-1.5 max-w-[180px]">
                        {r.imovel_id ? (
                          <EntityLink entityType="imovel" entityId={r.imovel_id} label={r.imovel_nome || '—'} showIcon={false} />
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-slate-600 whitespace-nowrap">{r.matricula || '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{fmtArea(r.area_terreno_m2)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-600 whitespace-nowrap">{fmtPct(r.percentual_participacao)}</td>
                      <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{TIPO_PROP_LABEL[r.tipo_propriedade] || r.tipo_propriedade || '—'}</td>
                      <td className="px-3 py-1.5 text-slate-600 truncate max-w-[150px]" title={r.area_nome || ''}>{r.area_nome || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-1.5 text-slate-600 truncate max-w-[150px]" title={r.contato_email || r.contato_mobile || ''}>
                        {isPJ ? <span className="text-slate-400">—</span> : (r.contato_email || r.contato_mobile || <span className="text-slate-400">—</span>)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
